/**
 * A list page's last query (filters, page, page size), kept for this tab so the bar's Back to that list lands on the
 * same filters and page: `useListReturn()` on the list remembers its search; PageReturn adds it to a plain path target
 * (a fixed `usePageReturn('/cpv/batches')` or the menu parent). A target that names its own query is used as it is.
 * The memory belongs to the signed-in person: `forgetListSearches()` runs on every login and logout (store/reducers/auth),
 * so a later person in the same tab never inherits another's filters or search. A session that expires reloads the app.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const memory = new Map();

/** Forget every remembered search (a login or logout: the next person starts from the lists' own defaults). */
export function forgetListSearches() {
  memory.clear();
}

/** Remember the search of `pathname` (an empty search forgets it). */
export function rememberListSearch(pathname, search) {
  if (search) memory.set(pathname, search); else memory.delete(pathname);
}

/** `path` with its list's remembered search, when it names none of its own. */
export function withListSearch(path) {
  if (typeof path !== 'string' || path.includes('?')) return path;
  return `${path}${memory.get(path) || ''}`;
}

/** On a list page: remember its current search for the Back of the pages it opens. */
export function useListReturn() {
  const { pathname, search } = useLocation();
  useEffect(() => { rememberListSearch(pathname, search); }, [pathname, search]);
}
